import { useCallback, useRef, useState } from 'react';
import {
  SEAT_DETAIL_MAX,
  type SeatDetail,
  type SeatDetailResponse,
  type SeatingAvailabilitySummary,
  type VenueMapVersionTopology,
} from '@gatecheckpro/contracts';
import { api } from '../../lib/api.ts';
import {
  NO_OVERLAY,
  VenueMapCanvas,
  batchOccurrences,
  indexSeatDetail,
  roomTopologyOf,
  seatAppearances,
  sectionAppearances,
  type SeatingOverlay,
} from '@gatecheckpro/venue-map';

/**
 * What a room shows, whoever is looking at it.
 *
 * A draft being edited and a night already selling are the same room with the
 * same live state on it; only the commercial controls differ. Keeping the
 * drawing and the asking in one place is what stops the read-only surface
 * quietly becoming a page of numbers with the room missing.
 */

/**
 * Live state for the chairs on screen, and only those.
 *
 * The whole room's worth used to arrive with the configuration — 39.98 MiB at
 * the contract's ceiling — which was correct and unusable. The canvas reports
 * what it drew; this asks about exactly that, and asks again when the drawn set
 * changes.
 */
export function useSeatDetail(versionPath: string): {
  readonly overlay: SeatingOverlay;
  readonly requestDetail: (occurrences: readonly string[]) => void;
} {
  const [detail, setDetail] = useState<readonly SeatDetail[]>([]);
  const asked = useRef('');
  const requestDetail = useCallback(
    (occurrences: readonly string[]) => {
      const key = occurrences.join(' ');
      if (key === asked.current) return;
      asked.current = key;
      if (occurrences.length === 0) {
        setDetail([]);
        return;
      }
      // Batched against the contract's limit, not the renderer's. Relying on
      // the server to truncate would draw a room with chairs quietly missing.
      void Promise.all(
        batchOccurrences(occurrences, SEAT_DETAIL_MAX).map((batch) =>
          api.request<SeatDetailResponse>(`${versionPath}/seat-detail`, {
            method: 'POST',
            body: { venueMapSeatIds: batch },
          }),
        ),
      )
        .then((responses) => {
          // An answer to a question nobody is asking any more: the person has
          // panned or zoomed since, and painting it would dress the chairs now
          // on screen with the state of the ones that were.
          if (asked.current !== key) return;
          setDetail(responses.flatMap((response) => response.seats));
        })
        .catch(() => setDetail([]));
    },
    [versionPath],
  );

  return { overlay: detail.length === 0 ? NO_OVERLAY : indexSeatDetail(detail), requestDetail };
}

/** The server's aggregate for the whole room, never a tally of what was drawn. */
export function SeatingCounts({ summary }: { readonly summary: SeatingAvailabilitySummary }) {
  return (
    <div className="gc-row" data-testid="seating-counts">
      <span className="gc-mono">On sale {summary.configuration.sellable.toLocaleString()}</span>
      <span className="gc-mono">Free {summary.configuration.available.toLocaleString()}</span>
      <span className="gc-mono">Being bought {summary.configuration.held.toLocaleString()}</span>
      <span className="gc-mono">Sold {summary.configuration.committed.toLocaleString()}</span>
      <span className="gc-mono">
        Not for sale {summary.configuration.excluded.toLocaleString()}
      </span>
    </div>
  );
}

/**
 * The room, dressed with the server's answers.
 *
 * Selection is optional because a live configuration is looked at rather than
 * worked on. Everything else is identical: the same aggregates on the sections
 * when level of detail suppresses chairs, and the same bounded ask for the
 * chairs it does draw.
 */
export function SeatingRoomCanvas({
  mapView,
  summary,
  overlay,
  onDrawnSeatsChange,
  selected,
  onSeatToggle,
}: {
  readonly mapView: VenueMapVersionTopology;
  readonly summary: SeatingAvailabilitySummary;
  readonly overlay: SeatingOverlay;
  readonly onDrawnSeatsChange: (occurrences: readonly string[]) => void;
  readonly selected?: ReadonlySet<string> | undefined;
  readonly onSeatToggle?: ((seatKey: string) => void) | undefined;
}) {
  const occurrenceByKey = new Map(mapView.seats.map((seat) => [seat.seatKey, seat.seatId]));
  return (
    <VenueMapCanvas
      topology={roomTopologyOf(mapView)}
      seatOverlay={seatAppearances(mapView.seats, overlay)}
      sectionOverlay={sectionAppearances(mapView.sections, summary)}
      onDrawnSeatsChange={(seatKeys) =>
        onDrawnSeatsChange(
          seatKeys
            .map((key) => occurrenceByKey.get(key))
            .filter((id): id is string => id !== undefined),
        )
      }
      selectedSeatKeys={selected ?? new Set()}
      onSeatToggle={onSeatToggle}
    />
  );
}
